/**
 * RiskHeatmap — 10x10 impact-vs-probability heatmap built with MUI Grid.
 *
 * Each cell shows the count of risks at that `(impact, probability)` pair.
 * Cell color is graded by exposure — `(impact * probability) / 100` — so the
 * top-right HIGH region glows red, the diagonal is amber, and the bottom-left
 * LOW region is green. Cells with zero risks are dimmed.
 */

import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import type { OrgRiskHeatmapCell } from "../Types/RiskManagementTypes";
import { levelColor, levelFromExposure, computeExposure } from "./RiskScoreBadge";

interface RiskHeatmapProps {
  cells: OrgRiskHeatmapCell[];
  /** Total cell side in px (the whole grid is 10x this). */
  cellSize?: number;
}

const RiskHeatmap: React.FC<RiskHeatmapProps> = ({ cells, cellSize = 32 }) => {
  // Lookup table for O(1) per-cell retrieval.
  const lookup = new Map<string, number>();
  cells.forEach((c) =>
    lookup.set(`${c.impact}-${c.probability}`, c.count)
  );

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        {/* Y axis label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            mr: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Impact (1-10)
          </Typography>
        </Box>
        <Box>
          {/* Rows: impact 10 at top → 1 at bottom for natural read. */}
          {Array.from({ length: 10 }, (_, rowIdx) => 10 - rowIdx).map((impact) => (
            <Box key={impact} sx={{ display: "flex" }}>
              <Box
                sx={{
                  width: 18,
                  height: cellSize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  pr: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {impact}
                </Typography>
              </Box>
              {Array.from({ length: 10 }, (_, c) => c + 1).map((probability) => {
                const count = lookup.get(`${impact}-${probability}`) ?? 0;
                const exposure = computeExposure(impact, probability);
                const color = levelColor(levelFromExposure(exposure));
                return (
                  <Tooltip
                    key={probability}
                    title={`Impact ${impact} × Probability ${probability} — ${count} risk(s) • exposure ${
                      Number.isInteger(exposure) ? exposure : exposure.toFixed(2)
                    }`}
                  >
                    <Box
                      sx={{
                        width: cellSize,
                        height: cellSize,
                        bgcolor: count > 0 ? color : `${color}22`,
                        opacity: count > 0 ? 0.85 : 0.4,
                        color: "#fff",
                        border: "1px solid #fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "default",
                      }}
                    >
                      {count > 0 ? count : ""}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
          <Box sx={{ display: "flex", pl: "18px" }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
              <Box
                key={p}
                sx={{
                  width: cellSize,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {p}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", pl: "18px" }}
          >
            Probability (1-10)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RiskHeatmap;
